/**
 * Botpress Webchat Integration
 * Handles product information extraction and webchat initialization
 */

// Wait for an element to appear (simple polling)
function waitForElement(selector, timeout = 5000, interval = 100) {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const timer = setInterval(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(timer);
        resolve(el);
      } else if (Date.now() - start > timeout) {
        clearInterval(timer);
        reject(new Error(`Timeout waiting for ${selector}`));
      }
    }, interval);
  });
}

// Extract product information from the page title
function extractProductInfoFromTitle() {
  const pathname = window.location.pathname;

  // Detect language from URL
  const language = pathname.includes('/fr/') ? 'french' : 'english';

  const titleEl = document.querySelector('h2.h2.product-single__title');
  if (!titleEl) {
    return { productName: '', language };
  }

  // Clone so we can safely modify without touching the live DOM
  const clone = titleEl.cloneNode(true);

  // Remove the "product-type" link (e.g., "Black Friday 2025 Store")
  const productTypeLink = clone.querySelector('a.product-type');
  if (productTypeLink) productTypeLink.remove();

  // Whatever text remains is the real product name
  const productName = clone.textContent.replace(/\s+/g, ' ').trim();

  return { productName, language };
}

// Create and display the fake webchat interface
function createFakeWebchat() {
  const container = document.getElementById('bp-embedded-webchat');
  if (!container) return;

  const { productName } = extractProductInfoFromTitle();

  // Detect if it's French based on URL
  const isFrench = window.location.pathname.includes('/fr/');

  // Get customer name if available
  const customerName = window.SHOPIFY_CUSTOMER_NAME || '';
  const greeting = customerName ? (isFrench ? `Salut ${customerName}!` : `Hey ${customerName}!`) : (isFrench ? 'Salut!' : 'Hey there!');

  // Translations
  const messages = {
    english: `${greeting} I'm Alix AI - your shopping assistant for all things alcohol-free 🥂. ${productName || 'this product'} is a great choice!!`,
    french: `${greeting} Je suis Alix AI - votre assistant pour tout ce qui est sans alcool 🥂. ${productName || 'ce produit'} est un excellent choix!!`
  };

  const placeholders = {
    english: 'Type a message...',
    french: 'Écrivez un message...'
  };

  const buttonLabels = {
    english: {
      Description: 'Can you describe this product?',
      Notes: 'What are the tasting notes?',
      Origin: 'Where does this come from?',
      Vendor: 'Who makes this product?',
      Size: 'What sizes are available?',
      Ingredients: 'Can you tell me about the ingredients?',
      Type: 'What type of product is this?',
      AlcoholVol: 'What is the alcohol volume?',
      WhyBuy: 'Why should I buy this product?',
      BestOccasions: 'What are some best suited occasions for this drink?',
      SimilarDrinks: 'Show me similar drinks'
    },
    french: {
      Description: 'Pouvez-vous décrire ce produit?',
      Notes: 'Quelles sont les notes de dégustation?',
      Origin: 'D\'où vient ce produit?',
      Vendor: 'Qui fabrique ce produit?',
      Size: 'Quelles tailles sont disponibles?',
      Ingredients: 'Pouvez-vous me parler des ingrédients?',
      Type: 'Quel type de produit est-ce?',
      AlcoholVol: 'Quel est le volume d\'alcool?',
      WhyBuy: 'Pourquoi devrais-je acheter ce produit?',
      BestOccasions: 'Quelles sont les meilleures occasions pour cette boisson?',
      SimilarDrinks: 'Montrez-moi des boissons similaires'
    }
  };

  const lang = isFrench ? 'french' : 'english';
  const labels = buttonLabels[lang];

  // Create a wrapper for the fake webchat that overlays on top
  const fakeWrapper = document.createElement('div');
  fakeWrapper.id = 'fake-webchat-wrapper';
  fakeWrapper.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1000; background: white;';

  const headerTitles = {
    english: 'Alix Product Assistant',
    french: 'Assistant Produit Alix'
  };

  const fakeWebchatHTML = `
    <div id="fake-webchat" class="fake-webchat">
      <div class="fake-webchat-header">
        <span class="fake-webchat-title">${headerTitles[lang]}</span>
      </div>
      <div class="fake-webchat-body">
        <div class="fake-webchat-message">
          <div class="fake-webchat-avatar">
            <img src="https://upsidedrinks.ca/cdn/shop/files/upside_LOGO_800_x_400_px_2.png?v=1696776927&width=280" alt="${headerTitles[lang]}" class="fake-webchat-avatar-img">
          </div>
          <div class="fake-webchat-bubble">
            ${messages[lang]}
          </div>
        </div>
        <div class="fake-webchat-quick-reply">
          <div class="fake-webchat-quick-reply-scroll">
            <button class="fake-quick-reply-btn" data-intent="Description">${labels.Description}</button>
            <button class="fake-quick-reply-btn" data-intent="Notes">${labels.Notes}</button>
            <button class="fake-quick-reply-btn" data-intent="Origin">${labels.Origin}</button>
            <button class="fake-quick-reply-btn" data-intent="Vendor">${labels.Vendor}</button>
            <button class="fake-quick-reply-btn" data-intent="Size">${labels.Size}</button>
            <button class="fake-quick-reply-btn" data-intent="Ingredients">${labels.Ingredients}</button>
            <button class="fake-quick-reply-btn" data-intent="Type">${labels.Type}</button>
            <button class="fake-quick-reply-btn" data-intent="AlcoholVol">${labels.AlcoholVol}</button>
            <button class="fake-quick-reply-btn" data-intent="WhyBuy">${labels.WhyBuy}</button>
            <button class="fake-quick-reply-btn" data-intent="BestOccasions">${labels.BestOccasions}</button>
            <button class="fake-quick-reply-btn" data-intent="SimilarDrinks">${labels.SimilarDrinks}</button>
            <button class="fake-quick-reply-btn" data-intent="Description">${labels.Description}</button>
            <button class="fake-quick-reply-btn" data-intent="Notes">${labels.Notes}</button>
            <button class="fake-quick-reply-btn" data-intent="Origin">${labels.Origin}</button>
            <button class="fake-quick-reply-btn" data-intent="Vendor">${labels.Vendor}</button>
            <button class="fake-quick-reply-btn" data-intent="Size">${labels.Size}</button>
            <button class="fake-quick-reply-btn" data-intent="Ingredients">${labels.Ingredients}</button>
            <button class="fake-quick-reply-btn" data-intent="Type">${labels.Type}</button>
            <button class="fake-quick-reply-btn" data-intent="AlcoholVol">${labels.AlcoholVol}</button>
            <button class="fake-quick-reply-btn" data-intent="WhyBuy">${labels.WhyBuy}</button>
            <button class="fake-quick-reply-btn" data-intent="BestOccasions">${labels.BestOccasions}</button>
            <button class="fake-quick-reply-btn" data-intent="Show me Similar Drinks">${labels.SimilarDrinks}</button>
          </div>
        </div>
      </div>
      <div class="fake-webchat-footer">
        <input
          type="text"
          id="fake-webchat-input"
          class="fake-webchat-input"
          placeholder="${placeholders[lang]}"
        />
      </div>
    </div>
  `;

  fakeWrapper.innerHTML = fakeWebchatHTML;
  container.style.position = 'relative';
  container.appendChild(fakeWrapper);

  // Add event listeners
  setTimeout(() => {
    const input = document.getElementById('fake-webchat-input');
    const quickReplyBtns = document.querySelectorAll('.fake-quick-reply-btn');

    const handleSubmit = (initialIntent) => {
      if (!initialIntent.trim()) return;

      // First expand both the fake webchat and the container
      const fakeWebchat = document.getElementById('fake-webchat');
      const container = document.getElementById('bp-embedded-webchat');
      if (fakeWebchat) {
        fakeWebchat.classList.add('expanded');
        if (container) {
          container.classList.add('expanded');
        }
        // Wait for expansion animation to complete before launching real webchat
        setTimeout(() => {
          launchRealWebchat(initialIntent);
        }, 800); // Match the CSS transition duration
      } else {
        launchRealWebchat(initialIntent);
      }
    };

    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        handleSubmit(input.value);
      }
    });

    // Add click handlers for all quick reply buttons
    quickReplyBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const intent = btn.getAttribute('data-intent');
        handleSubmit(intent);
      });
    });
  }, 100);
}

// Create reset button for real webchat
function createResetButton() {
  const container = document.getElementById('bp-embedded-webchat');
  if (!container) return;

  // Check if button already exists
  if (document.getElementById('webchat-reset-btn')) return;

  const resetBtn = document.createElement('button');
  resetBtn.id = 'webchat-reset-btn';
  resetBtn.className = 'webchat-reset-btn';
  resetBtn.setAttribute('aria-label', 'Reset webchat');

  // Plus icon with text
  resetBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
    <span>New Chat</span>
  `;

  resetBtn.addEventListener('click', async () => {
    // Reset conversation
    if (typeof window.botpress !== 'undefined') {
      await window.botpress.restartConversation();
    }

    // Remove expand button
    const expandBtn = document.getElementById('webchat-expand-btn');
    if (expandBtn) {
      expandBtn.remove();
    }

    // Remove reset button
    resetBtn.remove();

    // Collapse if expanded
    if (container.classList.contains('modal-expanded')) {
      container.classList.remove('modal-expanded');
      document.body.classList.remove('webchat-modal-open');
    }

    // Remove any expanded state from fake webchat
    const fakeWebchat = document.getElementById('fake-webchat');
    if (fakeWebchat) {
      fakeWebchat.classList.remove('expanded');
      container.classList.remove('expanded');
    }

    // Show fake webchat wrapper again
    const fakeWrapper = document.getElementById('fake-webchat-wrapper');
    if (fakeWrapper) {
      fakeWrapper.style.display = 'block';
      fakeWrapper.classList.remove('fade-out');
      // Trigger reflow
      void fakeWrapper.offsetWidth;
      fakeWrapper.style.opacity = '1';
    } else {
      // Recreate fake webchat if it doesn't exist
      createFakeWebchat();
    }
  });

  container.appendChild(resetBtn);
}

// Create expand/collapse button for real webchat
function createExpandButton() {
  const container = document.getElementById('bp-embedded-webchat');
  if (!container) return;

  // Check if button already exists
  if (document.getElementById('webchat-expand-btn')) return;

  const expandBtn = document.createElement('button');
  expandBtn.id = 'webchat-expand-btn';
  expandBtn.className = 'webchat-expand-btn';
  expandBtn.setAttribute('aria-label', 'Expand webchat');

  // Expand icon (maximize)
  expandBtn.innerHTML = `
    <svg class="expand-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
    <svg class="collapse-icon" style="display: none;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  `;

  expandBtn.addEventListener('click', () => {
    const isExpanded = container.classList.contains('modal-expanded');

    if (isExpanded) {
      // Collapse
      container.classList.remove('modal-expanded');
      document.body.classList.remove('webchat-modal-open');
      expandBtn.querySelector('.expand-icon').style.display = 'block';
      expandBtn.querySelector('.collapse-icon').style.display = 'none';
      expandBtn.setAttribute('aria-label', 'Expand webchat');
    } else {
      // Expand
      container.classList.add('modal-expanded');
      document.body.classList.add('webchat-modal-open');
      expandBtn.querySelector('.expand-icon').style.display = 'none';
      expandBtn.querySelector('.collapse-icon').style.display = 'block';
      expandBtn.setAttribute('aria-label', 'Collapse webchat');
    }
  });

  container.appendChild(expandBtn);
}

// Launch the real Botpress webchat with all context
async function launchRealWebchat(initialIntent) {
  if (typeof window.botpress === 'undefined') {
    console.warn('Botpress webchat not loaded');
    return;
  }

  try {
    // Ensure the title is on the page before extracting
    await waitForElement('h2.h2.product-single__title');
  } catch (e) {
    console.warn('Product title not found:', e);
  }

  const { productName, language } = extractProductInfoFromTitle();

  // Get customer name if available
  const customerName = window.SHOPIFY_CUSTOMER_NAME || '';

  // Format customer name instruction
  let customerInstruction = '';
  if (customerName) {
    customerInstruction = language === 'french'
      ? `Adressez-vous à l'utilisateur comme ${customerName}`
      : `Address user as ${customerName}`;
  }

  // Send product information, customer name, and initial intent to chatbot
  const customPayload = {
    productName,
    language,
    initialIntent,
    customerInstruction
  };

  // Show loading overlay while restarting the conversation
  const fakeWrapper = document.getElementById('fake-webchat-wrapper');
  const loader = document.createElement('div');
  loader.id = 'fake-webchat-loading';
  if (fakeWrapper) fakeWrapper.appendChild(loader);

  // Start a fresh conversation now that the user has actually engaged
  await window.botpress.restartConversation();

  // Wait for the new conversation to be ready before sending the payload
  await new Promise(resolve => setTimeout(resolve, 500));

  loader.remove();

  // Send event first to get bot response ready
  window.botpress.sendEvent(customPayload);
  if (fakeWrapper) {
    fakeWrapper.classList.add('fade-out');
    // Wait for fade animation to complete before fully hiding
    await new Promise(resolve => setTimeout(resolve, 600));
    fakeWrapper.style.display = 'none';
  }

  // Create expand and reset buttons after real webchat is shown
  setTimeout(() => {
    createExpandButton();
    createResetButton();
  }, 100);
}

// Initialize when the script loads
function initializeBotpressWebchat() {
  if (typeof window.botpress === 'undefined') {
    console.warn('Botpress webchat not loaded');
    return;
  }

  window.botpress.on('webchat:ready', async function () {
    // Create and show the fake webchat overlay
    // Real webchat stays loaded in the background
    createFakeWebchat();
  });
}

// Initialize when the script loads
initializeBotpressWebchat();
